import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight, Printer } from "lucide-react";

import { cn } from "@/lib/utils";
import type { LineaPianoRead } from "@/types/m2";
import { coloreStampo, fmtMinuti, num } from "@/pages/M2/format";

/**
 * Vista a LISTA del piano (doc #4 §7): pianificazione sulla finestra, giorni
 * collassabili + navigatore + stampa PDF.
 *
 * ⚠️ **Solo le giornate che hanno lavorazioni.** La finestra del piano MES di
 * produzione dichiara 51 giorni, ma solo 15 hanno qualcosa dentro: elencare anche
 * gli altri voleva dire 22 accordéon che dicono «0 lavorazioni» (più 14 di
 * weekend), cioè un muro di barre grigie da scorrere prima di arrivare al lavoro.
 * I giorni saltati vengono **dichiarati** in una riga sopra la lista: nasconderli
 * in silenzio sarebbe far credere che il piano sia compatto.
 */
const DAY = 86_400_000;
const DOW = ["dom", "lun", "mar", "mer", "gio", "ven", "sab"];

/** Il gestionale esporta certe descrizioni con un `§` davanti (3.009 articoli su
 *  5.922 in anagrafica). È rumore dell'export, non un dato: non si mostra. */
function pulisci(d: string | null): string | null {
  if (!d) return null;
  const s = d.replace(/^§+\s*/, "").trim();
  return s || null;
}

export interface FiltroLista {
  /** `estrusione` | `confezionamento` | `cambi` | `sospette` | `stampo:Txxx` */
  chiave: string | null;
  /** Testo della ricerca viva, già in minuscolo. */
  cerca: string;
}

interface Riga {
  id: string;
  ora: string;
  code: string;
  desc: string | null;
  stampo: string | null;
  famiglia: string | null;
  velocita: number | null;
  qty: number | null;
  um: string | null;
  durataMin: number | null;
  durataSospetta: boolean;
  durataMotivo: string | null;
  durataAttesa: number | null;
  setupPrima: "stampo" | "ricetta" | null;
  setupDa: string | null;
  setupA: string | null;
}

interface LineaGiorno {
  lane: string;
  nome: string;
  estrusione: boolean;
  righe: Riga[];
}

interface Giorno {
  key: string;
  ts: number;
  totLav: number;
  cambiStampo: number;
  perStampo: Record<string, number>;
  linee: LineaGiorno[];
}

function tsOf(iso: string | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : null;
}
function dayKey(t: number): string {
  const d = new Date(t);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function oraDi(t: number): string {
  const d = new Date(t);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

type RigaApi = LineaPianoRead["righe"][number];

/** Il filtro passa prima della costruzione dei giorni: così un giorno che resta
 *  senza righe sparisce, invece di comparire vuoto. */
function passa(r: RigaApi, estrusione: boolean, f: FiltroLista): boolean {
  const c = f.chiave;
  if (c === "estrusione" && !estrusione) return false;
  if (c === "confezionamento" && estrusione) return false;
  if (c === "cambi" && !(estrusione && r.setup_tipo === "cambio_stampo")) return false;
  if (c === "sospette" && !r.durata_sospetta) return false;
  if (c?.startsWith("stampo:") && r.stampo_code !== c.slice(7)) return false;

  if (f.cerca) {
    const campi = [
      r.semilavorato_codice,
      r.materiale_codice,
      r.articolo_codice,
      pulisci(r.descrizione),
      r.ordine_mes,
      r.stampo_code,
    ];
    if (!campi.some((v) => v?.toLowerCase().includes(f.cerca))) return false;
  }
  return true;
}

function build(linee: LineaPianoRead[], filtro: FiltroLista): Giorno[] {
  type Item = { t0: number; lane: string; estr: boolean; r: RigaApi };
  const items: Item[] = [];
  for (const l of linee) {
    const estr = l.tipo === "estrusione";
    for (const r of l.righe) {
      const t0 = tsOf(r.finestra_da);
      if (t0 === null) continue;
      if (!passa(r, estr, filtro)) continue;
      items.push({ t0, lane: l.codice_macchina, estr, r });
    }
  }
  if (!items.length) return [];

  const perGiorno = new Map<string, Item[]>();
  for (const it of items) {
    const k = dayKey(it.t0);
    const arr = perGiorno.get(k);
    if (arr) arr.push(it);
    else perGiorno.set(k, [it]);
  }

  const giorni: Giorno[] = [];
  for (const [key, ditems] of [...perGiorno.entries()].sort(([a], [b]) => (a < b ? -1 : 1))) {
    const perStampo: Record<string, number> = {};
    let cambiStampo = 0;
    const linee_g: LineaGiorno[] = [];
    for (const l of linee) {
      const righeL = ditems
        .filter((i) => i.lane === l.codice_macchina)
        .sort((a, b) => a.t0 - b.t0);
      if (!righeL.length) continue;
      const estr = l.tipo === "estrusione";
      const righe: Riga[] = [];
      let prevStampo: string | null = null;
      let prevMat: string | null = null;
      let primo = true;
      for (const it of righeL) {
        const r = it.r;
        const stampo = r.stampo_code;
        const mat = r.materiale_codice;
        let setupPrima: "stampo" | "ricetta" | null = null;
        if (!primo && estr) {
          if (prevStampo && stampo && prevStampo !== stampo) setupPrima = "stampo";
          else if (prevMat && mat && prevMat !== mat) setupPrima = "ricetta";
        }
        if (setupPrima === "stampo") cambiStampo += 1;
        if (stampo) perStampo[stampo] = (perStampo[stampo] ?? 0) + 1;
        righe.push({
          id: r.id,
          ora: oraDi(it.t0),
          code: r.semilavorato_codice ?? r.materiale_codice ?? r.articolo_codice ?? "—",
          desc: pulisci(r.descrizione),
          stampo,
          famiglia: r.famiglia,
          velocita: r.velocita_kg_h,
          qty: r.quantita,
          um: r.um,
          durataMin: r.durata_min,
          durataSospetta: r.durata_sospetta,
          durataMotivo: r.durata_motivo,
          durataAttesa: r.durata_attesa_min,
          setupPrima,
          setupDa: prevStampo,
          setupA: stampo,
        });
        prevStampo = stampo;
        prevMat = mat;
        primo = false;
      }
      linee_g.push({
        lane: l.codice_macchina,
        nome: l.nome ?? `Linea ${l.codice_macchina}`,
        estrusione: estr,
        righe,
      });
    }
    giorni.push({
      key,
      ts: new Date(`${key}T00:00:00`).getTime(),
      totLav: ditems.length,
      cambiStampo,
      perStampo,
      linee: linee_g,
    });
  }
  return giorni;
}

/** Le giornate lavorative senza niente dentro, fra la prima e l'ultima: il
 *  numero che giustifica di non elencarle. */
function giorniSaltati(giorni: Giorno[]): { lavorativi: number; weekend: number } {
  if (giorni.length < 2) return { lavorativi: 0, weekend: 0 };
  const presenti = new Set(giorni.map((g) => g.key));
  let lavorativi = 0;
  let weekend = 0;
  for (let t = giorni[0].ts; t <= giorni[giorni.length - 1].ts; t += DAY) {
    if (presenti.has(dayKey(t))) continue;
    const dow = new Date(t).getDay();
    if (dow === 0 || dow === 6) weekend += 1;
    else lavorativi += 1;
  }
  return { lavorativi, weekend };
}

export function ListaPiano({
  linee,
  filtro = { chiave: null, cerca: "" },
}: {
  linee: LineaPianoRead[];
  filtro?: FiltroLista;
}) {
  const giorni = useMemo(() => build(linee, filtro), [linee, filtro]);
  const saltati = useMemo(() => giorniSaltati(giorni), [giorni]);
  const [chiusi, setChiusi] = useState<Set<string>>(new Set());

  // Giorni collassati di default (feedback Enrico, #18), tranne il primo: una
  // lista tutta chiusa non mostra di che cosa è fatta.
  useEffect(() => {
    setChiusi(new Set(giorni.slice(1).map((g) => g.key)));
  }, [giorni]);

  const maxBar = Math.max(1, ...giorni.map((g) => g.totLav));
  const tuttiAperti = chiusi.size === 0;

  function toggle(key: string) {
    setChiusi((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  }
  function toggleTutti() {
    setChiusi(tuttiAperti ? new Set(giorni.map((g) => g.key)) : new Set());
  }
  function vaiA(key: string) {
    document.getElementById(`giorno-${key}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  if (!giorni.length) {
    return (
      <p className="py-12 text-center text-sm text-muted-foreground">
        {filtro.chiave || filtro.cerca
          ? "Nessuna lavorazione con questo filtro."
          : "Nessuna lavorazione datata nel piano."}
      </p>
    );
  }

  const totLav = giorni.reduce((a, g) => a + g.totLav, 0);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <span className="text-[12.5px] text-muted-foreground">
          <b className="text-foreground">{giorni.length}</b>{" "}
          {giorni.length === 1 ? "giornata" : "giornate"} con lavorazioni ·{" "}
          {num(totLav)} {totLav === 1 ? "lavorazione" : "lavorazioni"}
          {saltati.lavorativi > 0 && (
            <>
              {" "}
              · {saltati.lavorativi} giornate lavorative senza niente in mezzo non sono elencate
              {saltati.weekend > 0 && ` (più ${saltati.weekend} di weekend)`}
            </>
          )}
        </span>
        <div className="ml-auto flex gap-2">
          <button
            type="button"
            onClick={toggleTutti}
            className="rounded-md border bg-card px-3 py-1.5 text-xs font-medium hover:bg-accent/40"
          >
            {tuttiAperti ? "Comprimi tutto" : "Espandi tutto"}
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 rounded-md border bg-card px-3 py-1.5 text-xs font-medium hover:bg-accent/40"
          >
            <Printer className="size-3.5" /> Stampa PDF
          </button>
        </div>
      </div>

      {/* navigatore: una casella per giornata reale, non per giorno di calendario */}
      <div className="flex gap-1 overflow-x-auto rounded-lg border bg-card p-2.5 print:hidden">
        {giorni.map((g) => {
          const d = new Date(g.ts);
          return (
            <button
              key={g.key}
              type="button"
              onClick={() => vaiA(g.key)}
              title={`${g.totLav} lavorazioni`}
              className="flex w-[52px] shrink-0 flex-col items-center rounded-md border border-transparent px-1 py-1 hover:bg-muted"
            >
              <span className="text-[10px] uppercase text-muted-foreground">{DOW[d.getDay()]}</span>
              <span className="text-sm font-semibold">{d.getDate()}</span>
              <span className="mt-1 flex h-6 items-end gap-px">
                {Object.entries(g.perStampo)
                  .slice(0, 5)
                  .map(([st, c]) => (
                    <span
                      key={st}
                      className="w-[5px] rounded-sm"
                      style={{ height: 4 + (c / maxBar) * 22, background: coloreStampo(st).border }}
                    />
                  ))}
              </span>
            </button>
          );
        })}
      </div>

      <div className="space-y-4">
        {giorni.map((g) => {
          const chiuso = chiusi.has(g.key);
          const d = new Date(g.ts);
          const dataLunga = d.toLocaleDateString("it-IT", {
            weekday: "long",
            day: "numeric",
            month: "long",
          });
          return (
            <section key={g.key} id={`giorno-${g.key}`} className="scroll-mt-2">
              <button
                type="button"
                onClick={() => toggle(g.key)}
                className="sticky top-0 z-10 flex w-full items-center gap-2 border-b bg-muted px-1 py-2 text-left"
              >
                {chiuso ? (
                  <ChevronRight className="size-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="size-4 text-muted-foreground" />
                )}
                <span className="text-[15px] font-semibold capitalize">{dataLunga}</span>
                <span className="text-xs text-muted-foreground">
                  · {g.totLav} {g.totLav === 1 ? "lavorazione" : "lavorazioni"}
                  {g.cambiStampo > 0 &&
                    ` · ${g.cambiStampo} ${g.cambiStampo === 1 ? "cambio" : "cambi"} stampo`}
                </span>
              </button>
              {!chiuso && (
                <div className="mt-2 grid grid-cols-1 gap-x-4 gap-y-3 lg:grid-cols-2">
                  <div className="space-y-3">
                    {g.linee
                      .filter((l) => l.estrusione)
                      .map((l) => (
                        <LineaCard key={l.lane} linea={l} />
                      ))}
                  </div>
                  <div className="space-y-3">
                    {g.linee
                      .filter((l) => !l.estrusione)
                      .map((l) => (
                        <LineaCard key={l.lane} linea={l} />
                      ))}
                  </div>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

const GRIGLIA = "grid-cols-[46px_minmax(0,1fr)_96px_82px_66px]";

function LineaCard({ linea }: { linea: LineaGiorno }) {
  const cambi = linea.righe.filter((r) => r.setupPrima === "stampo").length;
  return (
    <div>
      <div className="mb-1 flex items-baseline gap-2 px-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {linea.nome}
        </span>
        <span className="text-[10.5px] text-muted-foreground">
          {linea.righe.length} {linea.righe.length === 1 ? "lavorazione" : "lavorazioni"}
          {cambi > 0 && ` · ${cambi} ${cambi === 1 ? "cambio" : "cambi"} stampo`}
        </span>
      </div>
      <div className="overflow-hidden rounded-lg border bg-card">
        <div
          className={cn(
            "grid gap-2 border-b px-3 py-2 text-[10px] uppercase tracking-wide text-muted-foreground",
            GRIGLIA
          )}
        >
          <div>Ora</div>
          <div>{linea.estrusione ? "Semilavorato" : "Prodotto"}</div>
          <div>Famiglia</div>
          <div className="text-right">Quantità</div>
          <div className="text-right">Durata</div>
        </div>
        {linea.righe.map((r) => (
          <div key={r.id}>
            {r.setupPrima && (
              <div
                className={cn(
                  "px-3 py-1 text-[11px]",
                  r.setupPrima === "stampo"
                    ? "bg-amber-50 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300"
                    : "bg-muted text-muted-foreground"
                )}
              >
                {r.setupPrima === "stampo" ? (
                  <>
                    <b>+40&apos;</b> cambio stampo · {r.setupDa} → {r.setupA}
                  </>
                ) : (
                  <>
                    <b>+10&apos;</b> cambio ricetta (stesso stampo)
                  </>
                )}
              </div>
            )}
            <div
              className={cn(
                "grid items-center gap-2 border-b px-3 py-2 text-[13px] last:border-b-0",
                GRIGLIA
              )}
            >
              <div className="tabular-nums text-[12px] text-muted-foreground">{r.ora}</div>
              <div className="flex min-w-0 items-start gap-2">
                <span
                  className="mt-1 size-2.5 shrink-0 rounded-sm"
                  style={{ background: coloreStampo(r.stampo).border }}
                />
                <span className="min-w-0">
                  <span className="block truncate font-mono text-[12px] font-semibold">
                    {r.code}
                  </span>
                  {r.desc && (
                    <span className="block truncate text-[11px] text-muted-foreground">
                      {r.desc}
                    </span>
                  )}
                </span>
              </div>
              <div className="text-[11px]">
                {r.famiglia ? (
                  <span className="rounded border bg-secondary px-1.5 py-0.5">{r.famiglia}</span>
                ) : (
                  <span className="italic text-muted-foreground">famiglia ignota</span>
                )}
                {/* La velocità kg/h è dell'estrusione: il backend non la manda
                    sulle righe di confezionamento, dove non vuol dire niente. */}
                {r.velocita != null && (
                  <span className="ml-1 text-muted-foreground">
                    {num(r.velocita)} kg/h
                  </span>
                )}
              </div>
              <div className="text-right tabular-nums text-[12.5px]">
                {r.qty != null ? `${num(r.qty)} ${r.um ?? ""}` : "—"}
              </div>
              <div
                className={cn(
                  "flex items-center justify-end gap-1 text-right tabular-nums text-[12px]",
                  r.durataSospetta
                    ? "font-semibold text-red-700 dark:text-red-400"
                    : "text-muted-foreground"
                )}
                title={
                  r.durataSospetta
                    ? `Durata non credibile: ${r.durataMotivo ?? ""}`
                    : r.durataAttesa
                      ? `Stima dalla velocità: ~${fmtMinuti(r.durataAttesa)}`
                      : undefined
                }
              >
                {fmtMinuti(r.durataMin)}
                {r.durataSospetta && <AlertTriangle className="size-3 shrink-0" />}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
